/**
 * Diese Datei enthält NUR öffentliche Client-Konfiguration.
 * Server-/API-Keys gehören ausschließlich in die Umgebungsvariablen des Servers.
 *
 * Es werden hier bewusst KEINE geheimen Schlüssel hinterlegt:
 * - Projekt-ID und Endpoint sind öffentliche Konfiguration.
 * - Sessions entstehen ausschließlich über `createEmailPasswordSession()` (E-Mail + Passwort).
 */

import { Client, Databases, Account, ID, Query } from 'appwrite';

const endpoint = import.meta.env.VITE_APPWRITE_ENDPOINT || 'https://appwrite.chargedesk.de/v1';
const projectId = import.meta.env.VITE_APPWRITE_PROJECT_ID || '69f92be900208208e766';

/**
 * Datenbank-ID. DATABASE_ID und APPWRITE_DB_ID sind bewusst Aliase desselben
 * Wertes, damit beide Importpfade immer dieselbe Datenbank addressieren.
 */
export const DATABASE_ID = import.meta.env.VITE_APPWRITE_DATABASE_ID || '69f92d670018e137d391';
export const APPWRITE_DB_ID = DATABASE_ID;

export const COLLECTIONS = {
  EXPENSES: import.meta.env.VITE_APPWRITE_EXPENSES_COLLECTION_ID || 'expenses_v2',
  CUSTOMERS: import.meta.env.VITE_APPWRITE_CUSTOMERS_COLLECTION_ID || 'customers_v2',
  CREDITS: import.meta.env.VITE_APPWRITE_CREDITS_COLLECTION_ID || 'credits_v2',
  TASKS: import.meta.env.VITE_APPWRITE_TASKS_COLLECTION_ID || 'tasks_v2',
  AUDIT: import.meta.env.VITE_APPWRITE_AUDIT_COLLECTION_ID || 'audit_logs',
  COMMISSION: import.meta.env.VITE_APPWRITE_COMMISSION_COLLECTION_ID || 'commission_history',
  TASK_STATUSES: import.meta.env.VITE_APPWRITE_TASK_STATUSES_COLLECTION_ID || 'task_statuses',
};

const client = new Client()
  .setEndpoint(endpoint)
  .setProject(projectId);

export const databases = new Databases(client);
export const account = new Account(client);

export { client, ID, Query };

/**
 * Ermittelt den aktuell angemeldeten Benutzer.
 * @returns {Promise<object|null>} Account-Objekt oder null, wenn keine Session existiert.
 */
export const getCurrentAccount = async () => {
  try {
    return await account.get();
  } catch (e) {
    // Kein Login / abgelaufene Session / Netzwerkfehler -> nicht angemeldet
    return null;
  }
};

/**
 * Erstellt eine Appwrite-Session für E-Mail + Passwort.
 *
 * WICHTIG (Appwrite-SDK >= 16, Server 1.8):
 * `account.createSession(userId, secret)` ist der TOKEN-Login
 * (`POST /account/sessions/token`) und erwartet eine echte userId.
 * Eine E-Mail-Adresse ist dort kein gültiger userId -> der Server antwortet mit
 * "Invalid `userId` param" (HTTP 400) und der Login schlägt fehl.
 * Der E-Mail/Passwort-Login läuft ausschließlich über `createEmailPasswordSession()`
 * (`POST /account/sessions/email`).
 *
 * @param {string} email
 * @param {string} password
 * @returns {Promise<object>} Session-Modell
 */
export const createEmailPasswordSession = async (email, password) => {
  if (typeof account.createEmailPasswordSession === 'function') {
    return account.createEmailPasswordSession(email, password);
  }
  // Rückfall für ältere SDK-Versionen (< 16), dort war createSession(email, pw) korrekt.
  return account.createSession(email, password);
};

/**
 * Prüft, ob die aktuelle Session wirklich Daten lesen darf.
 *
 * `GET /v1/account` ist auf dieser Instanz für Projekt-User eingeschränkt
 * (Rolle "guests" ohne "account"-Scope) und liefert daher verlässlich 401.
 * Ein authentifizierter Lesezugriff auf die Datenbank ist der belastbarere
 * Nachweis fuer eine gueltige Session.
 *
 * @returns {Promise<boolean>} true, wenn die Session Daten lesen darf
 */
export const verifySession = async () => {
  try {
    await databases.listDocuments(APPWRITE_DB_ID, COLLECTIONS.CUSTOMERS, [Query.limit(1)]);
    return true;
  } catch (e) {
    return false;
  }
};

export const testConnection = async () => {
  try {
    console.log('🔍 Teste Verbindung zu Appwrite:', endpoint);
    await databases.listDocuments(APPWRITE_DB_ID, COLLECTIONS.CUSTOMERS, [Query.limit(1)]);
    console.log('✅ Appwrite Verbindung erfolgreich!');
    return { ok: true };
  } catch (e) {
    // 401 = Server erreichbar, aber noch keine Session (Gast-Phase vor dem Login).
    // Das ist KEIN Verbindungsfehler und darf keine Fehlermeldung erzeugen.
    const unauthorized = e?.code === 401 || /unauthorized/i.test(e?.message || '');
    if (unauthorized) {
      console.log('ℹ️ Appwrite erreichbar, aber noch keine Session (401) – kein Verbindungsfehler.');
      return { ok: true, unauthorized: true, error: e?.message };
    }
    console.error('❌ Appwrite Verbindungsfehler:', e?.message);
    return { ok: false, error: e?.message };
  }
};
